/**
 * Dynamic Offers Collection - Refatorado com Statsig Manager
 */

export {};

import {
  ModelManager,
  getOrCreateStatsigManager,
  buildModelRequestPayload,
  Logger,
  syncDebugFlag,
} from './statsig-manager.js';

const MODEL_KEY = "statsig:test:dyo:collection";
const EXPERIMENT = {
  title: "DyoCollection",
  name: "promo-collection-do-v1",
  gate: "promo-collection",
  layer: "promo-collection",
};

const DEBUG = false; // Altere para false para desativar logs deste teste

if (!customElements.get("dyo-collection"))
  customElements.define(
    "dyo-collection",
    class DynamicOffersCollection extends HTMLElement {
      customerId?: string;
      statsigGate: string;
      statsigLayer: string;
      modelLink: string | null;
      statsigStableId: string | null;
      allocatedExperiment: string | null;
      updateTimeout: number;
      isGateEnabled: boolean;
      blockedUTMs: string;
      REQUEST_TIMEOUT_MS: number;
      modelManager: ModelManager;
      statsigManager: Awaited<ReturnType<typeof getOrCreateStatsigManager>>;
      elements: any;
      cart: string | null;
      cartItemsId: string | null;

      constructor() {
        super();
        this.customerId = this.getAttribute("data-customer-id") || null;
        this.statsigGate = this.getAttribute("data-statsig-gate") || EXPERIMENT.gate;
        this.statsigLayer = this.getAttribute("data-statsig-layer") || EXPERIMENT.layer;
        this.modelLink = null;
        this.statsigStableId = null;
        this.allocatedExperiment = null;
        this.updateTimeout = 0;
        this.isGateEnabled = this.getAttribute("data-is-gate-enabled") === "true";
        this.blockedUTMs = this.getAttribute("data-blocked-utms") || "";
        this.REQUEST_TIMEOUT_MS = 5000;
        this.modelManager = new ModelManager();
      }

      async connectedCallback() {
        syncDebugFlag(EXPERIMENT.name, DEBUG);

        this.elements = {
          serverContainer: this.querySelector("#do-collections-server-container"),
          clientContainer: this.querySelector("#do-collections-client-container"),
          clientContent: this.querySelector("#do-collections-client-content"),
          guestContainer: this.querySelector("#do-collections-guest-container"),
          loadingContainer: this.querySelector("#do-collections-loading-container"),
          errorMessage: this.querySelector("#do-collections-error-message"),
          skeletonItems: this.querySelectorAll(".do-collection-skeleton-item"),
          blockedSquares: this.querySelectorAll(".do-collection-blocked-square"),
        };

        try {
          this.statsigManager = await getOrCreateStatsigManager();
          await this.runTest();
        } catch (error) {
          this.logError(`[Statsig][${EXPERIMENT.name}] StatsigManager not ready`, false, error);
          this.handleServerComponent();
        }
      }

      async runTest() {
        try {
          Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Iniciando execução do Dynamic Offers Collection`);

          if (!this.validateUserUTMs(this.blockedUTMs)) {
            Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] UTM bloqueado - fallback para server-side`);
            this.handleServerComponent();
            return;
          }
          Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] UTMs validados com sucesso`);

          let statsigData = await this.getStatsigData();
          if (!statsigData) {
            this.handleServerComponent();
            return;
          }

          const { userLoginRequired } = statsigData;

          if (userLoginRequired && !this.customerId) {
            this.handleLoginRequired();
            return;
          }

          if (!this.modelLink) {
            Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Nenhuma modelLink encontrada - fallback para server-side`);
            this.handleServerComponent();
            return;
          }

          let collectionData = this.loadStoredCollection();
          if (!collectionData) {
            Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Buscando dados do modelo...`);
            collectionData = await this.fetchDOCollectionData();
            Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Dados do modelo recebidos:`, collectionData);
            if (!collectionData) {
              this.handleServerComponent();
              return;
            }
          }

          Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Buscando product cards...`);
          const productCards = await this.fetchProductCards(collectionData["product_links"]);
          Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Product cards recebidos:`, productCards);
          if (!productCards) {
            this.handleServerComponent();
            return;
          }

          Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Atualizando a coleção no DOM...`);
          this.updateDOCollection(productCards, collectionData);
          this.handleClientComponent();
        } catch (error) {
          this.logError(`[Statsig][${EXPERIMENT.name}] Unexpected error in init()`, false, error);
          this.handleServerComponent();
        }
      }

      async getStatsigData() {
        try {
          await this.statsigManager.waitForInitialization();
        } catch (error) {
          this.logError(`[Statsig][${EXPERIMENT.name}] Failed waiting for statsig`, false, error);
          return null;
        }

        try {
          if (this.isGateEnabled && !this.statsigManager.checkGate(this.statsigGate)) {
            this.handleServerComponent();
            return null;
          }

          const formatLayer = await this.statsigManager.getLayer(this.statsigLayer);

          this.allocatedExperiment = formatLayer.allocatedExperimentName;
          this.modelLink = formatLayer.get("model-link");
          this.updateTimeout = formatLayer.get("update-timeout");
          const userLoginRequired = formatLayer.get("user-login-required");

          return { userLoginRequired };
        } catch (error) {
          this.logError(`[Statsig][${EXPERIMENT.name}] Failed to fetch Statsig Data`, false, error);
          return null;
        }
      }

      validateUserUTMs(blockedUTMs: string): boolean {
        let blockedList = blockedUTMs.split(",").map((utm) => {
          const [source, medium, campaign] = utm.split("|").map((s) => s.trim().toLowerCase());
          return { source, medium, campaign: campaign || null };
        });

        let userUTMs = JSON.parse(
          localStorage.getItem("statsig:utms") ||
          localStorage.getItem("dynamic_offers_utms") ||
          "[]"
        ).map((utm) => ({
          utm_source: utm.utm_source ? utm.utm_source.trim().toLowerCase() : "",
          utm_medium: utm.utm_medium ? utm.utm_medium.trim().toLowerCase() : "",
          utm_campaign: utm.utm_campaign ? utm.utm_campaign.trim().toLowerCase() : "",
          timestamp: utm.timestamp,
        }));
        if (userUTMs.length === 0) return true;

        const lastUTM = userUTMs[userUTMs.length - 1];
        if (new Date(lastUTM.timestamp) < new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)) {
          return true;
        }

        return !blockedList.some((b) => {
          const match = b.source === lastUTM.utm_source && b.medium === lastUTM.utm_medium;
          return b.campaign ? match && b.campaign === lastUTM.utm_campaign : match;
        });
      }

      loadStoredCollection() {
        const exp = parseInt(localStorage.getItem("DOCollectionUpdatePromoAt") || "0", 10);
        const data = JSON.parse(localStorage.getItem("DOCollectionData") || "null");
        if (data && exp && exp < Date.now()) {
          localStorage.removeItem("DOCollectionData");
          localStorage.removeItem("DOCollectionUpdatePromoAt");
          return null;
        }
        return data;
      }

      async fetchDOCollectionData() {
        const payload = await buildModelRequestPayload({
          customer_id: this.customerId,
          stable_id: this.statsigStableId,
          allocated_experiment: this.allocatedExperiment,
        }, ["customer_id", "stable_id", "allocated_experiment", "cartItemsIds", "utms", "products", "collections", "cart"]);

        try { 
          const data = await this.modelManager.getModel(
            this.modelLink,
            payload,
            { timeout: this.REQUEST_TIMEOUT_MS },
            MODEL_KEY
          );

          Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Dados do modelo recebidos:`, data);
          return data;
        } catch (error) {
          this.logError(`[Statsig][${EXPERIMENT.name}] Failed to fetch DOCollectionData via ModelManager`, false, error);
          return null;
        }
      }

      async fetchProductCards(urls: string[]) {
        try {
          const results = await Promise.all(urls.map(async (url) => {
            const res = await fetch(url + (url.includes("?") ? "&" : "?") + "view=promoCollection");
            return res.ok ? await res.text() : null;
          }));
          return results.filter(Boolean);
        } catch (error) {
          this.logError(`[Statsig][${EXPERIMENT.name}] Failed to fetch product cards`, false, error);
          return null;
        }
      }

      updateDOCollection(cards: string[], data: any) {
        cards.forEach((card, index) => {
          const el = new DOMParser().parseFromString(`<li class="grid__item">${card}</li>`, "text/html").body.firstElementChild;
          const cupom = el.querySelector("cupom-countdown");
          if (cupom && data["discount_message"] && data["discount"]) {
            cupom.setAttribute("data-message", data["discount_message"]);
            cupom.setAttribute("data-discount", data["discount"]);
          }
          el.querySelector(".product-card-wrapper .card").setAttribute("data-gtm-element-position", String(index + 1));
          this.elements.clientContent.appendChild(el);
        });
      }

      handleLoginRequired() {
        this.elements.guestContainer.style.display = "flex";
        this.elements.skeletonItems.forEach(i => i.style.display = "none");
        this.elements.blockedSquares.forEach(s => s.style.display = "flex");
      }

      handleServerComponent() {
        this.elements.loadingContainer.style.display = "none";
        this.elements.serverContainer.style.display = "block";
      }

      handleClientComponent() { 
        this.elements.loadingContainer.style.display = "none";
        this.elements.clientContainer.style.display = "block";
      }

      logError(message: string, showError: boolean = false, error?: Error) {
        if (showError) {
          this.elements.errorMessage.textContent = "Erro carregando. Tente novamente mais tarde.";
          this.elements.skeletonItems.forEach(i => i.style.display = "none");
        }
        Logger.error(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] ${message}`, error);
      }
    }
  );


/*

  INÍCIO
   │
   ▼
[Validação de UTM]
   │
   ├── (UTM bloqueado?)
   │       └─► Sim → Fallback server-side (handleServerComponent)
   │       └─► Não → Continua
   │
   ▼
[Statsig Manager - Espera Inicialização]
   │
   ▼
[Check Gate e Layer]
   │
   ├── (Gate fechado?)
   │       └─► Sim → Fallback server-side
   │       └─► Não → Continua
   │
   ▼
[Verifica se precisa de login]
   │
   ├── (userLoginRequired && !customerId?)
   │       └─► Sim → Mostra UI de login (handleLoginRequired)
   │       └─► Não → Continua
   │
   ▼
[Verifica se há modelLink]
   │
   ├── (Tem modelLink?)
   │       └─► Não → Fallback server-side
   │       └─► Sim → Continua
   │
   ▼
[Tenta carregar DOCollectionData do localStorage]
   │
   ├── (Dados válidos?)
   │       └─► Sim → Usa dados locais → Vai para renderização
   │       └─► Não → Faz requisição ao modelo
   │
   ▼
[Requisição ao modelo]
   │
   ├── (Falha?)
   │       └─► Sim → Fallback server-side
   │       └─► Não → Continua
   │
   ▼
[Busca product cards]
   │
   ├── (Falha?)
   │       └─► Sim → Fallback server-side
   │       └─► Não → Continua
   │
   ▼
[Renderiza coleção no client]
   │
   ▼
[FIM]

*/